/*!
 * qikify-cart-drawer-a11y.js  v2  (2026-08-10)
 * ------------------------------------------------------------------
 * Accessibility repairs for the Qikify Sticky Cart drawer (`.qsc2-drawer`).
 *
 * WHAT THIS CHANGES, stated plainly because v1 understated it:
 * this script SETS ARIA ATTRIBUTES **AND TAKES OVER KEYBOARD BEHAVIOUR** while
 * the drawer is open. It cancels the default action of every Tab keypress and
 * moves focus itself, it can move focus in response to `focusin`, and it closes
 * the drawer on Escape. It does not alter layout, styling or DOM structure.
 *
 * ── Defect 1: no dialog semantics ────────────────────────────────────────────
 * The drawer ships with no role, no aria-modal and no accessible name, so it is
 * not announced as a dialog. Fixed by setting them.
 *
 * ── Defect 2: forward focus containment fails when accessiBe is present ──────
 * Measured on shop.bigthirst.com, 2026-08-10, by A/B/A reversal on one store
 * (theme and app version therefore held constant), with this patch disabled and
 * the tab-order boundary forced explicitly rather than inferred:
 *
 *     accessiBe loaded   → forward boundary ESCAPES into <access-widget-ui>
 *     accessiBe blocked  → forward boundary WRAPS correctly
 *     accessiBe loaded   → ESCAPES again
 *
 * Backward containment worked in all three runs. So Qikify's trap does exist and
 * does work; something about accessiBe defeats it in the forward direction only.
 *
 * The MECHANISM is NOT established and is deliberately not asserted here. The
 * widget sits BEFORE the drawer in document order and no element on the page has
 * a positive tabindex, so simple DOM-order leakage does not explain it. It is
 * equally possible that Qikify's trap is naive about late-injected content and
 * that any similar third-party script would break it the same way. Do not
 * describe this to a client as "the vendor broke it" on this evidence alone.
 *
 * Fixed here by driving the whole tab cycle on the capture phase, so containment
 * holds regardless of what else on the page handles keys or moves focus. A
 * boundary-only trap was tried first and still leaked, so the full cycle is
 * deliberate, not overreach.
 *
 * ── Defect 3: cross-sell buttons are not distinguishable ─────────────────────
 * Every recommended product's button reads aria-label="Add to cart button", so
 * they are indistinguishable from one another. (Weaker than a missing name:
 * WCAG 2.4.6 at AA, not 4.1.2 at A.)
 *
 * ── Defect 4: focus stranded on close ────────────────────────────────────────
 * Closing left focus on an element inside the now-hidden drawer. Focus is now
 * returned to whatever opened it.
 *
 * ⚠️ NOT VERIFIED WITH A REAL SCREEN READER. Focus position was measured
 * programmatically. VoiceOver / NVDA / JAWS virtual-cursor behaviour, and switch
 * or sip-and-puff input, have not been tested.
 *
 * Remove once Qikify ships these upstream.
 * Connected Systems.
 */
(function () {
  'use strict';

  // Guard against double installation. Two copies would both handle Tab and
  // advance focus twice per keypress, silently skipping every other control.
  if (window.__csQikifyA11y) return;
  window.__csQikifyA11y = '2026-08-10';

  var DRAWER = '.qsc2-drawer';
  var OPEN_BODY_CLASS = 'qsc2-drawer-opened';
  var CLOSE_BTN = '.qsc2-drawer-header__close-button';
  var GENERIC = /^add to cart( button)?$/i;
  var FOCUSABLE = [
    'a[href]', 'button', 'input:not([type=hidden])', 'textarea', 'select',
    'iframe', '[contenteditable="true"]'
  ].map(function (s) { return s + ':not([tabindex="-1"])'; })
    .concat('[tabindex]:not([tabindex="-1"])').join(',');

  var lastTrigger = null;
  var wasOpen = false;
  var recoveries = 0;
  var recoveryWindow = 0;

  function drawerEl() { return document.querySelector(DRAWER); }
  function isOpen() { return document.body.classList.contains(OPEN_BODY_CLASS); }

  // If a nested modal is open inside the drawer, it owns the keyboard. Stand down
  // rather than fight it.
  function nestedModalOpen(d) {
    return !!d.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not(' + DRAWER + ')');
  }

  function focusables(d) {
    var list = [].slice.call(d.querySelectorAll(FOCUSABLE)).filter(function (e) {
      if (e.disabled || e.closest('[aria-hidden="true"]')) return false;
      if (e.tabIndex < 0) return false;
      if (!e.getClientRects().length) return false;
      var s = getComputedStyle(e);
      return s.visibility !== 'hidden' && s.display !== 'none';
    });
    // Honour positive tabindex ordering: those come first, ascending, then the
    // tabindex-0 elements in document order. Nothing on these stores currently
    // uses positive tabindex, but a future app could.
    var pos = list.filter(function (e) { return e.tabIndex > 0; })
      .sort(function (a, b) { return a.tabIndex - b.tabIndex; });
    var zero = list.filter(function (e) { return e.tabIndex === 0; });
    return pos.concat(zero);
  }

  // document.activeElement reports the HOST when focus is inside a shadow root,
  // and the host may not itself be in the list. Map back to the entry that
  // contains it instead of treating it as "focus is somewhere unknown" and
  // bouncing to the first control.
  function indexOfActive(list) {
    var a = document.activeElement;
    var i = list.indexOf(a);
    if (i !== -1) return i;
    for (var k = 0; k < list.length; k++) {
      if (list[k] === a || (a && list[k].contains(a))) return k;
    }
    return -1;
  }

  function semantics(d) {
    if (d.getAttribute('role') !== 'dialog') d.setAttribute('role', 'dialog');
    if (d.getAttribute('aria-modal') !== 'true') d.setAttribute('aria-modal', 'true');
    if (!(d.getAttribute('aria-label') || '').trim() && !d.getAttribute('aria-labelledby')) {
      var lbl = d.querySelector('.qsc2-drawer-header__label');
      d.setAttribute('aria-label', (lbl && (lbl.textContent || '').trim()) || 'Your cart');
    }
  }

  function labelCrossSells(d) {
    d.querySelectorAll('.qsc2-r-product').forEach(function (card) {
      var t = card.querySelector('.qsc2-r-product__product-title');
      var b = card.querySelector('button.qsc2-btn');
      if (!t || !b) return;
      var name = (t.textContent || '').trim().replace(/\s+/g, ' ');
      if (!name) return;
      var current = (b.getAttribute('aria-label') || '').trim();
      if (current && !GENERIC.test(current) && !b.hasAttribute('data-cs-a11y')) return;
      var want = 'Add ' + name + ' to cart';
      if (current === want) return;
      b.setAttribute('aria-label', want);
      b.setAttribute('data-cs-a11y', '1');
    });
  }

  document.addEventListener('keydown', function (e) {
    var d = drawerEl();
    if (!d || !isOpen() || nestedModalOpen(d)) return;

    // Escape closes. A dialog that declares aria-modal is expected to, and Qikify
    // does not implement it. Delegate to the app's own close control so its state
    // handling and animation run normally.
    if (e.key === 'Escape' || e.keyCode === 27) {
      var btn = d.querySelector(CLOSE_BTN);
      if (btn) { e.preventDefault(); btn.click(); }
      return;
    }

    if (e.key !== 'Tab' && e.keyCode !== 9) return;
    var f = focusables(d);
    if (!f.length) return;

    e.preventDefault();
    var i = indexOfActive(f);
    var next = (i === -1)
      ? (e.shiftKey ? f[f.length - 1] : f[0])
      : f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length];
    if (next) next.focus();
  }, true);

  // Backstop for focus moved by something other than Tab: a re-render dropping
  // focus to <body>, or another script relocating it. Rate limited so that if a
  // competing focus manager fights back we lose gracefully instead of pinning the
  // browser in a focus ping-pong.
  document.addEventListener('focusin', function (e) {
    if (!isOpen()) return;
    var d = drawerEl();
    if (!d || d.contains(e.target) || nestedModalOpen(d)) return;

    var now = Date.now();
    if (now - recoveryWindow > 1000) { recoveryWindow = now; recoveries = 0; }
    if (++recoveries > 5) return; // something else insists; stop fighting

    var f = focusables(d);
    if (f.length) f[0].focus();
  }, true);

  document.addEventListener('click', function (e) {
    if (isOpen()) return;
    var t = e.target && e.target.closest && e.target.closest('button,a,[role=button]');
    if (t) lastTrigger = t;
  }, true);

  function sync() {
    var d = drawerEl();
    if (!d) return;
    var open = isOpen();

    if (open) {
      semantics(d);
      labelCrossSells(d);
      if (!wasOpen) {
        setTimeout(function () {
          if (!isOpen()) return;
          var dd = drawerEl();
          if (dd && !dd.contains(document.activeElement)) {
            var f = focusables(dd);
            if (f.length) f[0].focus();
          }
        }, 150);
      }
    } else if (wasOpen) {
      // Never leave focus on an element inside the closed drawer.
      var stranded = !document.activeElement
        || document.activeElement === document.body
        || d.contains(document.activeElement);
      if (stranded) {
        var target = (lastTrigger && document.contains(lastTrigger) && lastTrigger.offsetParent !== null)
          ? lastTrigger
          : document.querySelector('.qsc2-stickybtn, .qsc2-section-sticky');
        if (target) { try { target.focus(); } catch (err) { /* gone with the re-render */ } }
      }
    }
    wasOpen = open;
  }

  // Qikify can replace the drawer node wholesale, which would strand an observer
  // bound to the old one. Watch a stable ancestor and rebind when the node changes.
  var contentObserver = null;
  var observedNode = null;

  function bindContentObserver() {
    var d = drawerEl();
    if (!d || d === observedNode) return;
    if (contentObserver) contentObserver.disconnect();
    observedNode = d;
    var timer = null;
    contentObserver = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var dd = drawerEl();
        if (dd && isOpen()) { semantics(dd); labelCrossSells(dd); }
      }, 60);
    });
    // childList only: our own attribute writes cannot retrigger this.
    contentObserver.observe(d, { childList: true, subtree: true });
  }

  function attach() {
    if (!drawerEl()) return false;
    new MutationObserver(function () { sync(); }).observe(document.body, {
      attributes: true, attributeFilter: ['class'],
    });
    new MutationObserver(function () { bindContentObserver(); }).observe(document.body, {
      childList: true, subtree: false,
    });
    bindContentObserver();
    sync();
    return true;
  }

  if (!attach()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (attach() || ++tries > 100) clearInterval(poll);
    }, 200);
  }
})();
